import React from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { ChevronRight } from 'lucide-react'

import { border, font, space } from 'settings/tokens'

/** Row height. iOS/Artsy settings rows sit at 56; below ~48 the tap target suffers. */
export const FIELD_ROW_HEIGHT = 56

/**
 * Leading-slot width.
 *
 * 28, not `ListRow`'s 40. Matching that rail was the first instinct — a row that
 * leads to a list putting its label on the same x as that list's rows — but the
 * rail exists there to align a 40px AVATAR with an 18px glyph, and these rows
 * carry a 20px glyph and nothing else. At 40 the measured gap from glyph to
 * label was ~45px, which read as two separate columns rather than one labelled
 * row. The alignment it bought is also invisible: the list it would align with
 * is on a different screen.
 */
export const FIELD_ROW_LEADING_SIZE = 28

interface FieldRowProps {
  /** Label, on the LEFT of the row. */
  label: string
  /**
   * Leading glyph, in a fixed-width slot before the label (VERA-315).
   *
   * This is what separates a row you NAVIGATE from a row you READ. On the
   * certificate page the facts table and the section rows had become the same
   * visual language doing two different jobs — label left, hairline, same
   * gutter, near-identical rhythm — with a 20px chevron at the far edge as the
   * only categorical signal. Facts have no leading column, so an icon rail is
   * read instantly, before any of the words are.
   *
   * Width matches `ListRow`'s leading slot, so labels sit on the same rail as
   * the Scans and Activity lists these rows lead to.
   */
  icon?: React.ReactNode
  /**
   * Trailing content between the value and the chevron — a count, a badge.
   * Right-aligned and never shrinks.
   */
  meta?: React.ReactNode
  /** Run the row's hairline edge-to-edge, ignoring the page gutter. */
  fullBleed?: boolean
  /** Resolved display value, on the RIGHT. When empty, `placeholder` shows muted. */
  value?: string | null
  /** Prompt shown (muted) when there is no value, e.g. "Add". */
  placeholder?: string
  /** Render the value as a masked password dot string instead of text. */
  masked?: boolean
  /**
   * Accepted and ignored for display — see the note on "(optional)" in the
   * component doc. Kept in the API because callers legitimately know which
   * fields are optional and that may drive behaviour later.
   */
  optional?: boolean
  error?: boolean
  onClick: () => void
}

/**
 * One row of a settings list: `label ............ value ›`.
 *
 * ## Why this is no longer a labelled box
 *
 * It used to render a stacked label ABOVE a filled, bordered, 40px-tall box —
 * i.e. it looked exactly like a text input. Three problems, in order of weight:
 *
 * 1. **False affordance.** The row is read-only; tapping it opens a bottom
 *    sheet. Something drawn as an input invites a tap-to-type that never
 *    happens, and the grey fill additionally reads as *disabled*, so a filled
 *    box saying "Add" managed to look both editable and switched off at once.
 * 2. **Form layout for list content.** Label-above-control is right when you
 *    are filling a form. This is a list of settings you mostly read, and a list
 *    row puts the label and its value on ONE line — which is also why you can
 *    scan the column of values without reading the labels again.
 * 3. **Vertical cost.** The stacked form cost ~85px per field (measured pitch
 *    on the live page); six fields filled a phone viewport. At 56px the same
 *    six fit with room for the next group's heading, so the page reads as
 *    grouped sections rather than an endless column of grey slabs.
 *
 * ## "(optional)" is deliberately not rendered
 *
 * It appeared on 5 of the 6 General rows, which inverts the signal: when nearly
 * every row is marked optional, the marking stops distinguishing anything and
 * the one REQUIRED field is what becomes invisible. Requiredness is also only
 * actionable at edit time, and the edit drawer already enforces it. An empty
 * row showing "Add" says "nothing here yet" without a second label saying so.
 */
export const FieldRow: React.FC<FieldRowProps> = ({
  label,
  icon,
  meta,
  fullBleed = false,
  value,
  placeholder = 'Add',
  masked = false,
  error = false,
  onClick,
}) => {
  const theme = useTheme()
  const hasValue = masked || Boolean(value)

  const activate = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={activate}
      sx={{
        minHeight: FIELD_ROW_HEIGHT,
        gap: `${space.lg}px`,
        cursor: 'pointer',
        // `!important` is what makes this hold (the shorthand is incidental):
        // loses to index.css's `#vera-shadow-root *` rule (ID specificity beats
        // emotion's sx class). Not Tailwind preflight — that is disabled here.
        borderBottom: `1px solid ${border.subtle} !important`,
        '&:last-of-type': { borderBottom: '1px solid transparent !important' },
        // Full-bleed hover/press feedback, pulled back out to the row's edges so
        // the highlight covers the whole tap target rather than just the text.
        //
        // `fullBleed` pulls further — past the page's 16px gutter — so the
        // hairline runs edge to edge. That is the second half of the
        // navigate-vs-read distinction: inset rules read as belonging to the
        // column of content, edge-to-edge rules read as a list.
        mx: fullBleed ? `-${space.lg}px` : `-${space.sm}px`,
        px: fullBleed ? `${space.lg}px` : `${space.sm}px`,
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: `${theme.shape.borderRadius}px`,
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.text.primary}`,
          outlineOffset: '-2px',
        },
      }}
    >
      <Stack direction='row' alignItems='center' sx={{ minWidth: 0, gap: `${space.md}px` }}>
        {icon && (
          <Box
            sx={{
              width: FIELD_ROW_LEADING_SIZE,
              minWidth: FIELD_ROW_LEADING_SIZE,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              // Left-aligned in the slot, not centred: the glyph should line up
              // with the label column's left edge on every row.
              justifyContent: 'flex-start',
              color: theme.palette.text.primary,
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: `${font.body}px`,
            color: theme.palette.text.primary,
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
      </Stack>

      {/* minWidth:0 is what actually lets the value ellipsis inside a flex row —
          without it this column refuses to shrink below its content width and
          pushes the chevron off the row. Values here are unbounded (an email, a
          phone, a country), so the value truncates and the label never does. */}
      <Stack direction='row' alignItems='center' sx={{ minWidth: 0, gap: `${space.sm}px` }}>
        {meta}
        <Typography
          noWrap
          sx={{
            fontSize: `${font.body}px`,
            color: error
              ? theme.palette.error.main
              : hasValue
                ? theme.palette.text.secondary
                : theme.palette.text.disabled,
            letterSpacing: masked ? '3px' : 'normal',
            minWidth: 0,
          }}
        >
          {masked ? '••••••••' : value || placeholder}
        </Typography>
        <ChevronRight
          size={20}
          strokeWidth={1.5}
          color={theme.palette.text.disabled}
          style={{ flexShrink: 0 }}
        />
      </Stack>
    </Stack>
  )
}
